import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as React from 'react';
import { ReactComponent as XIcon } from 'src/assets/tailwind/x-close.svg';
import { cn } from 'src/utils/utils';

import { Button } from './button';

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'tw:fixed tw:inset-0 tw:z-1250 tw:bg-[#0000001A] tw:data-[state=open]:animate-in tw:data-[state=closed]:animate-out tw:data-[state=closed]:fade-out-0 tw:data-[state=open]:fade-in-0',
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

interface DialogContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  hideDefaultClose?: boolean;
  hideOverlay?: boolean;
  overlayProps?: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>;
}
const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(
  (
    {
      className,
      children,
      hideDefaultClose = false,
      hideOverlay = false,
      overlayProps,
      ...props
    },
    ref
  ) => (
    <DialogPortal>
      {hideOverlay ? null : <DialogOverlay {...overlayProps} />}
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          'tw:fixed tw:left-[50%] tw:top-[50%] tw:z-1250 tw:flex tw:flex-col tw:w-full tw:max-w-200 tw:translate-x-[-50%] tw:translate-y-[-50%] tw:border tw:border-border-primary tw:bg-primary tw:duration-200 tw:data-[state=open]:animate-in tw:data-[state=closed]:animate-out tw:data-[state=closed]:fade-out-0 tw:data-[state=open]:fade-in-0 tw:data-[state=closed]:zoom-out-95 tw:data-[state=open]:zoom-in-95 tw:data-[state=closed]:slide-out-to-left-1/2 tw:data-[state=closed]:slide-out-to-top-[48%] tw:data-[state=open]:slide-in-from-left-1/2 tw:data-[state=open]:slide-in-from-top-[48%] tw:rounded-3xl',
          className
        )}
        {...props}
      >
        {children}
        {!hideDefaultClose && (
          <DialogPrimitive.Close className="tw:absolute tw:top-2 tw:right-2">
            <Button
              leftIcon={XIcon}
              size="small"
              hierarchy="ghost"
              iconOnly
              className="tw:w-2xl"
            />
            <span className="tw:sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  )
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

/** A near-fullscreen card inset 12px on every side. Layout via `className`. */
const FullScreenDialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(
  (
    {
      className,
      children,
      hideDefaultClose = false,
      hideOverlay = false,
      ...props
    },
    ref
  ) => (
    <DialogPortal>
      {hideOverlay ? null : <DialogOverlay />}
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          'tw:fixed tw:inset-m tw:z-1250 tw:flex tw:items-center tw:justify-center tw:bg-primary tw:rounded-l tw:drop-shadow-[-8px_-8px_6px_rgba(0,0,0,0.02),8px_8px_6px_rgba(0,0,0,0.02)] tw:data-[state=open]:animate-in tw:data-[state=closed]:animate-out tw:data-[state=closed]:fade-out-0 tw:data-[state=open]:fade-in-0',
          className
        )}
        {...props}
      >
        {children}
        {!hideDefaultClose && (
          <DialogPrimitive.Close className="tw:absolute tw:top-2 tw:right-6">
            <Button
              leftIcon={XIcon}
              size="small"
              hierarchy="ghost"
              iconOnly
              className="tw:w-2xl"
            />
            <span className="tw:sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  )
);
FullScreenDialogContent.displayName = 'FullScreenDialogContent';

const DialogHeader = ({
  showBorder = true,
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { showBorder?: boolean }) => (
  <div
    className={cn(
      'tw:px-2xl tw:pt-m tw:pb-xl tw:flex tw:flex-col tw:space-y-1.5 tw:text-center tw:text-text-primary tw:sm:text-left',
      showBorder && 'tw:border-b tw:border-b-border-secondary',
      className
    )}
    {...props}
  />
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({
  showBorder = true,
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { showBorder?: boolean }) => (
  <div
    className={cn(
      'tw:px-2xl tw:py-m tw:flex tw:flex-col-reverse tw:sm:flex-row tw:sm:justify-end tw:sm:space-x-2',
      showBorder && 'tw:border-t tw:border-t-border-secondary',
      className
    )}
    {...props}
  />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      'tw:text-xl tw:leading-xl tw:font-bold tw:text-text-primary tw:tracking-tight',
      className
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('tw:text-sm tw:text-muted-foreground', className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
  FullScreenDialogContent
};
